import React, { useEffect } from 'react';
import { Typography, Row, Col, Card, Space, Button, Divider } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './TermsPage.css';

const { Title, Text, Paragraph } = Typography;

const TermsPage: React.FC = () => {
  const navigate = useNavigate();

  // Set page metadata
  useEffect(() => {
    document.title = 'Terms of Service | SNS Intelligence';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Terms of Service for SNS Intelligence. Learn about the terms and conditions governing your use of our pharmaceutical market intelligence platform.');
  }, []);

  return (
    <div className="terms-page">
      {/* Navigation */}
      <nav className="terms-navbar">
        <div className="nav-container">
          <div
            className="nav-logo"
            role="button"
            tabIndex={0}
            onClick={() => navigate('/')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                navigate('/');
              }
            }}
          >
            <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
          </div>
        </div>
      </nav>

      {/* Back to Home Button */}
      <div className="back-to-home-section">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-to-home-button"
        >
          Back to Home
        </Button>
      </div>

      <div className="terms-container">
        <Row justify="center">
          <Col xs={24} md={20} lg={16}>
            <div className="terms-header">
              <Title level={1} className="terms-title">
                Terms of Service
              </Title>
              <Text className="terms-subtitle">
                Last updated: {new Date().toLocaleDateString()}
              </Text>
            </div>

            <Card className="terms-card">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Paragraph>
                  These Terms of Service ("Terms") govern your access to and use of the SNS Intelligence website, platform, and related services (collectively, the "Service"), operated by SNS Research Group LLC, doing business as SNS Intelligence ("we", "us", "our").
                </Paragraph>
                <Paragraph>
                  By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, you may not access or use the Service.
                </Paragraph>

                <div>
                  <Title level={3} className="section-title">
                    1. Description of the Service
                  </Title>
                  <Paragraph>
                    SNS Intelligence provides a subscription-based pharmaceutical market intelligence platform offering market research, data analytics, drug pipeline insights, and related information for business and research purposes.
                  </Paragraph>
                  <Paragraph>
                    All information provided through the Service is for general informational and strategic purposes only and is not intended as medical, clinical, legal, or financial advice.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    2. Eligibility
                  </Title>
                  <Paragraph>
                    You must be at least 18 years old and have the legal authority to enter into these Terms.
                  </Paragraph>
                  <Paragraph>
                    If you are accessing the Service on behalf of a company or other legal entity, you represent that you have the authority to bind that entity to these Terms.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    3. Accounts and Access
                  </Title>
                  <Paragraph>
                    Access to the Service is restricted to registered users who have been granted access under a valid single-user, multi-user, or corporate license.
                  </Paragraph>
                  <Paragraph>
                    You agree to:
                  </Paragraph>
                  <ul className="terms-list">
                    <li>Provide accurate and complete registration information</li>
                    <li>Maintain the confidentiality of your login credentials</li>
                    <li>Be responsible for all activity conducted under your account</li>
                  </ul>
                  <Paragraph>
                    You may not:
                  </Paragraph>
                  <ul className="terms-list">
                    <li>Share accounts or login credentials</li>
                    <li>Allow access to unauthorized users</li>
                    <li>Circumvent licensing, access controls, or security mechanisms</li>
                  </ul>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    4. Permitted Use and Restrictions
                  </Title>
                  <Paragraph>
                    Subject to these Terms, SNS Intelligence grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for internal business or research purposes only.
                  </Paragraph>
                  <Paragraph>
                    You may not:
                  </Paragraph>
                  <ul className="terms-list">
                    <li>Share, distribute, publish, or make the Service or its content available to any unauthorized person</li>
                    <li>Copy, scrape, download, or redistribute data or content at scale</li>
                    <li>Resell, sublicense, or commercially exploit the Service or its outputs</li>
                    <li>Use the Service to build, train, or enhance competing products</li>
                    <li>Reverse engineer, decompile, or attempt to extract source code</li>
                    <li>Use automated tools or scripts to access the Service without prior written permission</li>
                  </ul>
                  <Paragraph>
                    Any unauthorized use may result in immediate suspension or termination of access.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    5. Intellectual Property
                  </Title>
                  <Paragraph>
                    All content, data, analysis, visualizations, reports, software, and documentation provided through the Service are the exclusive property of SNS Research Group LLC or its licensors and are protected by applicable intellectual property laws.
                  </Paragraph>
                  <Paragraph>
                    Except for the limited license expressly granted in these Terms, no rights or ownership interests are transferred to you.
                  </Paragraph>
                  <Paragraph>
                    All trademarks, logos, and brand elements are the property of SNS Research Group LLC.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    6. Data Sources and Accuracy Disclaimer
                  </Title>
                  <Paragraph>
                    The data and insights provided by SNS Intelligence are based on primary research, secondary research, third-party sources, and analytical models.
                  </Paragraph>
                  <Paragraph>
                    While we strive for accuracy, we:
                  </Paragraph>
                  <ul className="terms-list">
                    <li>Do not guarantee completeness, accuracy, or timeliness</li>
                    <li>Do not warrant that data is free from errors or omissions</li>
                    <li>Reserve the right to update, revise, or correct information at any time</li>
                  </ul>
                  <Paragraph>
                    All content provided through the Service is intended to provide general information and strategic insights only and does not constitute investment advice, medical advice, or financial advice.
                  </Paragraph>
                  <Paragraph>
                    You acknowledge that any decisions made using the Service are made at your own discretion and risk.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    7. Fees and Subscriptions
                  </Title>
                  <Paragraph>
                    Certain features of the Service may require payment of subscription fees.
                  </Paragraph>
                  <Paragraph>
                    All fees:
                  </Paragraph>
                  <ul className="terms-list">
                    <li>Are charged as described at the time of purchase</li>
                    <li>Are non-refundable unless expressly stated otherwise</li>
                    <li>May be modified with reasonable notice</li>
                  </ul>
                  <Paragraph>
                    Failure to pay applicable fees may result in suspension or termination of access.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    8. Suspension and Termination
                  </Title>
                  <Paragraph>
                    We may suspend or terminate your access to the Service, without notice, if you:
                  </Paragraph>
                  <ul className="terms-list">
                    <li>Violate these Terms</li>
                    <li>Misuse or abuse the platform or data</li>
                    <li>Engage in unauthorized sharing or distribution</li>
                    <li>Engage in unlawful or prohibited activities</li>
                  </ul>
                  <Paragraph>
                    Upon termination, your right to access and use the Service will immediately cease.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    9. Limitation of Liability
                  </Title>
                  <Paragraph>
                    To the maximum extent permitted by law:
                  </Paragraph>
                  <Paragraph>
                    SNS Intelligence disclaims all warranties, express or implied, regarding the Service and its content. We shall not be liable for any loss or damage, whether direct or indirect, incidental, consequential, foreseeable or not, arising out of or in connection with the use of or reliance on any information, analysis, estimates, opinions, or forecasts provided through the Service.
                  </Paragraph>
                  <Paragraph>
                    Our total liability for any claim relating to the Service shall not exceed the amount paid by you to SNS Intelligence in the twelve (12) months preceding the claim.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    10. Indemnification
                  </Title>
                  <Paragraph>
                    You agree to indemnify and hold harmless SNS Research Group LLC, its affiliates, and employees from any claims, liabilities, damages, or expenses arising out of:
                  </Paragraph>
                  <ul className="terms-list">
                    <li>Your use of the Service</li>
                    <li>Your violation of these Terms</li>
                    <li>Your violation of applicable laws or third-party rights</li>
                  </ul>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    11. Changes to These Terms
                  </Title>
                  <Paragraph>
                    We may update these Terms from time to time. Any changes will be effective upon posting to this page with an updated revision date.
                  </Paragraph>
                  <Paragraph>
                    Your continued use of the Service after changes are posted constitutes acceptance of the revised Terms.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    12. Governing Law
                  </Title>
                  <Paragraph>
                    These Terms shall be governed by and construed in accordance with the laws of the applicable jurisdiction, without regard to conflict of law principles.
                  </Paragraph>
                </div>

                <div>
                  <Title level={3} className="section-title">
                    13. Contact Information
                  </Title>
                  <Paragraph>
                    If you have any questions about these Terms:
                  </Paragraph>
                  <ul className="terms-list">
                    <li><strong>Email:</strong> <a href="mailto:info@snsintel.com">info@snsintel.com</a></li>
                    <li><strong>Subject:</strong> Terms of Service Inquiry</li>
                  </ul>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Footer */}
      <footer className="terms-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>&copy; 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
